import { useMemo } from "react";
import { Table, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";

import {
  AGREEMENT_LABEL,
  AGREEMENT_STYLE,
  buildAgreementIndex,
  cellClassName,
} from "./lineage";
import type {
  LineageFieldCluster,
  LineageMember,
  LineageSource,
} from "./lineage.types";

const FIELD_COL_WIDTH = 190;
const MEMBER_COL_WIDTH = 210;

interface LineageGridProps {
  members: LineageMember[];
  sources: LineageSource[];
  clusters: LineageFieldCluster[];
}

function FieldCell({ cluster }: { cluster: LineageFieldCluster }) {
  const style = AGREEMENT_STYLE[cluster.agreement];
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[12px] font-semibold text-[#1F2933]">
        {cluster.label}
      </span>
      <Tooltip title={style.tip}>
        <span
          className={`inline-flex w-fit cursor-help items-center rounded px-1.5 py-0.5 text-[10px] font-semibold ${style.pill}`}
        >
          {AGREEMENT_LABEL[cluster.agreement]}
          {cluster.distinct > 1 ? ` · ${cluster.distinct} values` : ""}
        </span>
      </Tooltip>
    </div>
  );
}

/** Column head for one raw record. Labelled by its source and position within
 *  that source — a wide source group scrolls its own header out of view, so a
 *  raw id here would leave the column unidentifiable. The id stays in the
 *  tooltip for anyone who needs to trace the record. */
function MemberHeader({
  member,
  label,
}: {
  member: LineageMember;
  label: string;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <Tooltip
        title={`Raw record ${member.raw_person_id}${member.nct_id ? ` · ${member.nct_id}` : ""}`}
      >
        <span className="cursor-help text-[11px] font-semibold whitespace-nowrap text-[#2B4752]">
          {label}
        </span>
      </Tooltip>
      {member.confidence_pct !== null && (
        <span className="text-[10px] font-semibold text-[#2B4752] tabular-nums">
          {member.confidence_pct}% tie
        </span>
      )}
    </div>
  );
}

export default function LineageGrid({
  members,
  sources,
  clusters,
}: LineageGridProps) {
  const agreementIndex = useMemo(
    () => buildAgreementIndex(clusters),
    [clusters],
  );

  const columns = useMemo<ColumnsType<LineageFieldCluster>>(() => {
    const fieldColumn = {
      title: (
        <span className="text-[11px] font-semibold text-[#556C75]">Field</span>
      ),
      key: "field",
      fixed: "left" as const,
      width: FIELD_COL_WIDTH,
      render: (_: unknown, cluster: LineageFieldCluster) => (
        <FieldCell cluster={cluster} />
      ),
    };

    const sourceGroups = sources.map((source) => ({
      title: (
        <span className="font-mcqueenGrotesk text-[12px] font-bold text-[#2B4752]">
          {source.source_label}
          <span className="ml-1.5 text-[11px] font-semibold text-[#556C75]">
            ×{source.member_count}
          </span>
        </span>
      ),
      key: source.source_system,
      children: members
        .filter((member) => member.source_system === source.source_system)
        .map((member, index, group) => ({
          title: (
            <MemberHeader
              member={member}
              label={
                group.length > 1
                  ? `${source.source_label} ${index + 1}`
                  : source.source_label
              }
            />
          ),
          key: member.raw_person_id,
          width: MEMBER_COL_WIDTH,
          render: (_: unknown, cluster: LineageFieldCluster) => {
            const value = member[cluster.field];
            const verdict =
              agreementIndex[cluster.field]?.[member.raw_person_id] ?? "empty";
            return (
              <div
                className={`-mx-2 -my-1 rounded px-2 py-1 text-[12px] ${cellClassName(cluster, verdict)}`}
              >
                {value ?? "—"}
              </div>
            );
          },
        })),
    }));

    return [fieldColumn, ...sourceGroups];
  }, [members, sources, agreementIndex]);

  return (
    <Table<LineageFieldCluster>
      dataSource={clusters}
      columns={columns}
      rowKey="field"
      size="small"
      bordered
      pagination={false}
      scroll={{ x: FIELD_COL_WIDTH + members.length * MEMBER_COL_WIDTH }}
    />
  );
}
